/**
 * Low bases are called out, never cut (M4S-5777, X76). Spec:
 * docs/pdlc/crosstabs-statistics-rows.md §3.6.
 *
 * The one home of the low-base rule:
 *
 *  - **By the question's own base.** A cell is low when fewer than the threshold of the column's
 *    respondents answered THIS question (counted raw, never weighted: the "Unweighted base" row a
 *    weighted table prints, X87), not when the column is
 *    small. A question asked of 20 people in a column of 200 is greyed; the column's other
 *    questions are not. (Before X76 a cell took its column's flag, BT-2088 §5.3; that choice is
 *    reversed here and recorded in docs/pdlc/crosstabs-oct6-uat-bugs.md.)
 *  - **Any threshold.** 30 by default, 50 or 100 at a click, or any whole number: the suppression
 *    threshold (`suppressionThreshold`, a statistics setting saved with the crosstab; its default
 *    and the letters floor live in `base-thresholds.ts`, X12).
 *  - **Greyed, not hidden.** Every value stays visible, greyed, with the reason on hover and in the
 *    legend. Hiding is a separate switch (`suppressionEnabled`), off by default, and hides exactly
 *    the cells that are greyed: on screen, in the chart and in every file.
 *  - **Letters follow X12.** No column is lettered under 30 answerers, and none under a higher
 *    threshold (`letterFloor`), so a grey cell never carries a letter.
 *
 * Pure.
 */

/** The thresholds the rail offers at a click; any other whole number is a custom one. */
export const LOW_BASE_PRESETS: readonly number[] = [30, 50, 100];

/** The smallest and largest custom threshold the rail accepts. */
export const LOW_BASE_MIN = 1;
export const LOW_BASE_MAX = 10_000;

export type LowBaseState = 'ok' | 'low' | 'hidden';

/** Whether a base of `n` (raw answerers) is low, and whether it is hidden. */
export function lowBaseState(n: number, opts: { threshold: number; hide: boolean }): LowBaseState {
    if (!(n < opts.threshold)) return 'ok';
    return opts.hide ? 'hidden' : 'low';
}

/** The state of a computed cell, whose `suppressed` flag compute set by this rule. */
export function cellLowState(cell: { suppressed: boolean }, hide: boolean): LowBaseState {
    if (!cell.suppressed) return 'ok';
    return hide ? 'hidden' : 'low';
}

/** A threshold as the rail or a request gave it, or null when it is not a whole number in range. */
export function parseLowBaseThreshold(raw: unknown): number | null {
    const n = typeof raw === 'string' && raw.trim() ? Number(raw) : raw;
    if (typeof n !== 'number' || !Number.isFinite(n)) return null;
    const whole = Math.round(n);
    return whole >= LOW_BASE_MIN && whole <= LOW_BASE_MAX ? whole : null;
}

/** The hover on a low cell. */
export function lowBaseReason(threshold: number, hide: boolean): string {
    return hide
        ? `Hidden: fewer than ${threshold} in this column answered this question (its unweighted base)`
        : `Low base: fewer than ${threshold} in this column answered this question (its unweighted base). Interpret with caution.`;
}

/** The legend line for a table (or a file) that holds at least one low cell. */
export function lowBaseLegendLine(threshold: number, hide: boolean): string {
    return hide
        ? `‡ = hidden: fewer than ${threshold} in the column answered the question (its unweighted base).`
        : `Grey = low base: fewer than ${threshold} in the column answered the question (its unweighted base); interpret with caution.`;
}

/**
 * Marks every cell of `rows` (but the base row, which is the number that explains it) low when
 * fewer than `threshold` of its column answered the question: `answerers` holds that raw count per
 * column id. A column missing from `answerers` keeps the flag it has.
 */
export function markLowBaseCells(
    rows: ReadonlyArray<{ kind: string; cells: Array<{ col: string; suppressed: boolean }> }>,
    answerers: Readonly<Record<string, number>>,
    threshold: number,
): void {
    for (const row of rows) {
        if (row.kind === 'base') continue;
        for (const cell of row.cells) {
            const n = answerers[cell.col];
            if (n !== undefined) cell.suppressed = n < threshold;
        }
    }
}

/** Whether any cell of the rows is low. */
export function hasLowCells(
    rows: ReadonlyArray<{ kind: string; cells: ReadonlyArray<{ suppressed: boolean }> }>,
): boolean {
    return rows.some((r) => r.kind !== 'base' && r.cells.some((c) => c.suppressed));
}
